import { CanvasAPI, ContainerAPI, UIModelAPI } from '../types/api.types';
import {
  updateHeader as updateHeaderAPI,
  updateFooter as updateFooterAPI,
  updateNavigation as updateNavigationAPI,
  updateCanvas as updateCanvasAPI,
  getCanvas as getCanvasAPI, createUIModelApi, updateUIModelApi,
  getUIModelById, deleteUIModelApi, copyUIModelApi, getThemeAPI, updateThemeAPI,
  imageIconUploadAPI
} from '../services/ApiHelper';
import {UIModelTheme} from "../components/ThemeEditor";

export class UIModelApiService {
  
  private mockUIModels: Map<string, UIModelAPI> = new Map();

  /**
   * Create UI Model
   */
  async createUIModel(configureId: string, data: FormData): Promise<{
    success: boolean;
    data: { model: UIModelAPI };
  }> {
    
    const response = await createUIModelApi(configureId, data);
    if (!response.done) {
      throw new Error(response.message ?? 'Error');
    }
    const newModel: UIModelAPI = response.data.model;
    this.mockUIModels.set(newModel.id, newModel);
    return {
      success: response.done,
      data: { model: newModel }
    };
  }
  /**
   * Create UI Model
   */
  async updateUIModel(configureId: string, modelId: string, data: FormData): Promise<{
    success: boolean;
    data: { model: UIModelAPI };
  }> {
    const response = await updateUIModelApi(configureId, modelId, data);
    if (!response.done) {
      throw new Error(response.message ?? 'Error');
    }
    const newModel: UIModelAPI = response.data.model;
    this.mockUIModels.set(newModel.id, newModel);
    return {
      success: true,
      data: { model: newModel }
    };
  }
  /**
   * Copy UI Model
   */
  async copyUIModel(configureId: string, modelId: string, data: FormData): Promise<{
    success: boolean;
    data: { model: UIModelAPI };
  }> {
    const response = await copyUIModelApi(configureId, modelId, data);
    if (!response.done) {
      throw new Error(response.message ?? 'Error');
    }
    const newModel: UIModelAPI = response.data.model;
    this.mockUIModels.set(newModel.id, newModel);
    return {
      success: true,
      data: { model: newModel }
    };
  }
  /**
   * Create UI Model
   */
  async deleteUIModel(configureId: string, modelId: string): Promise<{
    success: boolean;
  }> {
    const response = await deleteUIModelApi(configureId, modelId);
    return {
      success: response
    };
  }

  /**
   * Get UI Model
   */
  async getUIModel(modelId: string, uiModelId: string): Promise<{
    success: boolean;
    data: { model: UIModelAPI };
  }> {
    //await new Promise(resolve => setTimeout(resolve, this.mockDelay));
    
    const model = this.mockUIModels.get(uiModelId);
    
    if (!model) {
      throw new Error('Model not found');
    }
    
    return {
      success: true,
      data: { model }
    };
  }

  /**
 * Update Header - Real API with FormData support
 */
async updateHeader( configureId: string,  modelId: string,  header: any,  logoFile?: File, resetButtonIconFile?: File, closeButtonIconFile?: File ): Promise<{ success: boolean; data: { updated: boolean; timestamp: string };}> {
  
  try {
    const formData = new FormData();
    
    const headerConfig = {
      showHeader: header.showHeader,
      showTitle: header.showTitle,
      titleText: header.titleText,
      titleLayout: header.titleLayout,
      showResetButton: header.showResetButton,
      resetButtonText: header.resetButtonText,
      resetButtonShowIcon: header.resetButtonShowIcon,
      resetButtonShowTooltip: header.resetButtonShowTooltip,
      resetButtonTooltipText: header.resetButtonTooltipText || '',
      showCloseButton: header.showCloseButton,
      closeButtonText: header.closeButtonText,
      closeButtonShowIcon: header.closeButtonShowIcon,
      closeButtonShowTooltip: header.closeButtonShowTooltip,
      closeButtonTooltipText: header.closeButtonTooltipText || ''
    };
    
    formData.append('showHeader', headerConfig.showHeader.toString());
    formData.append('showTitle', headerConfig.showTitle.toString());
    formData.append('titleText', headerConfig.titleText);
    formData.append('titleLayout', headerConfig.titleLayout);
    formData.append('showResetButton', headerConfig.showResetButton.toString());
    formData.append('resetButtonText', headerConfig.resetButtonText);
    formData.append('resetButtonShowIcon', headerConfig.resetButtonShowIcon.toString());
    formData.append('resetButtonShowTooltip', headerConfig.resetButtonShowTooltip.toString());
    formData.append('resetButtonTooltipText', headerConfig.resetButtonTooltipText);
    formData.append('showCloseButton', headerConfig.showCloseButton.toString());
    formData.append('closeButtonText', headerConfig.closeButtonText);
    formData.append('closeButtonShowIcon', headerConfig.closeButtonShowIcon.toString());
    formData.append('closeButtonShowTooltip', headerConfig.closeButtonShowTooltip.toString());
    formData.append('closeButtonTooltipText', headerConfig.closeButtonTooltipText);
    
    if (logoFile) {
      formData.append('logoUrl', logoFile, logoFile.name);
    } else if (header.logoUrl) {  
      formData.append('logoUrl', header.logoUrl);
    }
    
    if (resetButtonIconFile) {
      formData.append('resetButtonIconUrl', resetButtonIconFile, resetButtonIconFile.name);
    } else if (header.resetButtonIconUrl) {
      formData.append('resetButtonIconUrl', header.resetButtonIconUrl);
    }
    
    if (closeButtonIconFile) {
      formData.append('closeButtonIconUrl', closeButtonIconFile, closeButtonIconFile.name);
    } else if (header.closeButtonIconUrl) {
      formData.append('closeButtonIconUrl', header.closeButtonIconUrl);
    }
    
    await updateHeaderAPI(configureId, modelId, formData);
    
    return {
      success: true,
      data: {
        updated: true,
        timestamp: new Date().toISOString()
      }
    };
  } catch (error) {
    console.error('Error updating header:', error);
    throw error;
  }
}

/**
 * Update Footer - Real API with FormData support
 */
async updateFooter( configureId: string,  modelId: string,  footer: any,  previousButtonIconFile?: File, nextButtonIconFile?: File, doneButtonIconFile?: File ): Promise<{ success: boolean; data: { updated: boolean; timestamp: string };}> {

  try {
    const formData = new FormData();

    const footerConfig = {
      showFooter: footer.showFooter,
      showDescription: footer.showDescription,
      descriptionText: footer.descriptionText,
      showPreviousButton: footer.showPreviousButton,
      previousButtonText: footer.previousButtonText,
      previousButtonShowIcon: footer.previousButtonShowIcon,
      previousButtonShowTooltip: footer.previousButtonShowTooltip,
      previousButtonTooltipText: footer.previousButtonTooltipText || '',
      showNextButton: footer.showNextButton,
      nextButtonText: footer.nextButtonText,
      nextButtonShowIcon: footer.nextButtonShowIcon,
      nextButtonShowTooltip: footer.nextButtonShowTooltip,
      nextButtonTooltipText: footer.nextButtonTooltipText || '',
      showDone: footer.showDone,
      doneButtonText: footer.doneButtonText || 'Done',
      doneButtonShowIcon: footer.doneButtonShowIcon,
      doneButtonShowTooltip: footer.doneButtonShowTooltip,
      doneButtonTooltipText: footer.doneButtonTooltipText || ''
    };

    formData.append('showFooter', footerConfig.showFooter.toString());
    formData.append('showDescription', footerConfig.showDescription.toString());
    formData.append('descriptionText', footerConfig.descriptionText);
    formData.append('showPreviousButton', footerConfig.showPreviousButton.toString());
    formData.append('previousButtonText', footerConfig.previousButtonText);
    formData.append('previousButtonShowIcon', footerConfig.previousButtonShowIcon.toString());
    formData.append('previousButtonShowTooltip', footerConfig.previousButtonShowTooltip.toString());
    formData.append('previousButtonTooltipText', footerConfig.previousButtonTooltipText);
    formData.append('showNextButton', footerConfig.showNextButton.toString());
    formData.append('nextButtonText', footerConfig.nextButtonText);
    formData.append('nextButtonShowIcon', footerConfig.nextButtonShowIcon.toString());
    formData.append('nextButtonShowTooltip', footerConfig.nextButtonShowTooltip.toString());
    formData.append('nextButtonTooltipText', footerConfig.nextButtonTooltipText);
    formData.append('showDone', footerConfig.showDone.toString());
    formData.append('doneButtonText', footerConfig.doneButtonText);
    formData.append('doneButtonShowIcon', footerConfig.doneButtonShowIcon.toString());
    formData.append('doneButtonShowTooltip', footerConfig.doneButtonShowTooltip.toString());
    formData.append('doneButtonTooltipText', footerConfig.doneButtonTooltipText);

    if (previousButtonIconFile) {
      formData.append('previousButtonIconUrl', previousButtonIconFile, previousButtonIconFile.name);
    } else if (footer.previousButtonIconUrl) {
      formData.append('previousButtonIconUrl', footer.previousButtonIconUrl);
    }

    if (nextButtonIconFile) {
      formData.append('nextButtonIconUrl', nextButtonIconFile, nextButtonIconFile.name);
    } else if (footer.nextButtonIconUrl) {
      formData.append('nextButtonIconUrl', footer.nextButtonIconUrl);
    }

    if (doneButtonIconFile) {
      formData.append('doneButtonIconUrl', doneButtonIconFile, doneButtonIconFile.name);
    } else if (footer.doneButtonIconUrl) {
      formData.append('doneButtonIconUrl', footer.doneButtonIconUrl);
    }

    await updateFooterAPI(configureId, modelId, formData);

    return {
      success: true,
      data: {
        updated: true,
        timestamp: new Date().toISOString()
      }
    };
  } catch (error) {
    console.error('Error updating footer:', error);
    throw error;
  }
}

  /**
   * Update Navigation - Real API
   */
  async updateNavigation(configureId: string, modelId: string, navigation: any): Promise<{
    success: boolean;
    data: { updated: boolean; timestamp: string };
}> {
    console.log('Updating navigation:', { configureId, modelId, navigation });
    if (navigation.navigation.groups.length === 0) {
        return {
            success: false,
            data: {
                updated: false,
                timestamp: new Date().toISOString()
            }
        };
    }
    
    try {
        const response = await updateNavigationAPI(configureId, modelId, navigation);
        
        if (response && response.done) {
          return {
              success: true,
              data: {
              updated: response.data.updated,
              timestamp: response.data.updatedAt || new Date().toISOString()
              }
          };
        }
        
        throw new Error('Failed to update navigation');
    } catch (error) {
        console.error('Error updating navigation:', error);
        throw error;
    }
}

  /**
   * Get Canvas by ID - Real API
   */
  async getCanvas(configureId: string, modelId: string, canvasId: string): Promise<{
  success: boolean;
  data: { 
    canvas: {
      pageId: string;
      groupId: string;
      containers: ContainerAPI[];
    } 
  };
}> {
  
  try {
    const response = await getCanvasAPI(configureId, modelId, canvasId);
    
    if (response && response.data) {
      const containers = (response.data.containers || []).map((container: any) => ({
        ...container,
        content: {
          items: [],
          ...container.content  
        }
      }));
      
      return {
        success: true,
        data: {
          canvas: {
            pageId: response.data.pageId || '',
            groupId: response.data.groupId || '',
            containers: containers
          }
        }
      };
    }
    
    throw new Error(`Canvas ${canvasId} not found`);
  } catch (error) {
    console.error('Error fetching canvas:', error);
    return {
      success: true,
      data: {
        canvas: {
          pageId: '',
          groupId: '',
          containers: []
        }
      }
    };
  }
}

/**
 * Get Canvas by ID
 */
    async getCanvasById(modelId: string, uiModelId: string, canvasId: string): Promise<{
    success: boolean;
    data: { canvas: CanvasAPI };
    }> {
        
        const mockCanvas: CanvasAPI = {
            pageId: '',
            groupId: '',
            containers: []
        };
        
        
        return {
            success: true,
            data: { canvas: mockCanvas }
        };
    }

    async getUIModelById(configureId: string, modelId: string): Promise<{ success: boolean; data: { model: UIModelAPI };}> {
      try {
        const response = await getUIModelById(configureId, modelId);
        
        if (response && response.done) {
          return {
            success: true,
            data: { model: response.data.model }
          };
        }
        
        throw new Error('Failed to fetch UI model');
      } catch (error) {
        console.error('Error fetching UI model:', error);
        throw error;
      }
    } 

    /**
     * Update Canvas
     */
    async updateCanvas(configureId: string, modelId: string, canvas: any, canvasId: string): Promise<{
      success: boolean;
      data: { updated: boolean; timestamp: string };}> {

        
        try {
          await updateCanvasAPI(configureId, modelId, canvasId, canvas);
          
          return {
            success: true,
            data: {
              updated: true,
              timestamp: new Date().toISOString()
            }
          };
        } catch (error) {
          console.error('Error updating canvas:', error);
          throw error;
        }
    }

    /**
     * Get Theme
     */
    async getTheme(projectId: string, modelId: string): Promise<{ success: boolean; data: UIModelTheme;}> {
      const response = await getThemeAPI(projectId, modelId);
      if (!response.done) {
        throw new Error(response.message ?? 'Error');
      }
      const uiModelThemeModel: UIModelTheme = response.data;
      return {
        success: response.done,
        data: uiModelThemeModel
      };
    }

    /**
     * Get Theme
     */
    async updateTheme(projectId: string, modelId: string, data: UIModelTheme | null): Promise<{ success: boolean;}> {
      const response = await updateThemeAPI(projectId, modelId, data);
      if (!response.done) {
        throw new Error(response.message ?? 'Error');
      }
      return {
        success: response.done,
      };
    }

    
    async uploadImageIcon (projectId: string, modelId: string, file: File, imageType: string = 'icon'): Promise<{ success: boolean; data: { imageUrl: string };}> {
      const response = await imageIconUploadAPI(projectId, modelId, file, imageType);

      // Extract imageUrl from nested response structure
      const imageUrl = response?.data?.imageUrl || '';

      return {
        success: true,
        data: { imageUrl }
      };
    }
}

export const uiModelApiService = new UIModelApiService();